import {
  DragDropContext,
  Draggable,
  Droppable,
  type OnDragEndResponder,
} from "@hello-pangea/dnd";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { useGetList, useTranslate, type DataProvider } from "ra-core";
import { useEffect, useMemo, useState } from "react";
import { Confirm } from "@/components/admin/confirm";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import type { Pipeline, PipelineStage, StageOutcome } from "../types";
import { byPosition } from "./byPosition";
import { useProjectAdmin } from "./useProjectAdmin";

const OUTCOMES: StageOutcome[] = ["open", "won", "lost"];

/** Gives the stages positions 10, 20, 30… in this order; only changed ones are saved. */
const renumber = (dataProvider: DataProvider, ordered: PipelineStage[]) =>
  Promise.all(
    ordered.flatMap((stage, i) =>
      stage.position === (i + 1) * 10
        ? []
        : [
            dataProvider.update<PipelineStage>("pipeline_stages", {
              id: stage.id,
              data: { position: (i + 1) * 10 },
              previousData: stage,
            }),
          ],
    ),
  );

/**
 * Stages of one pipeline: add, rename, drag to reorder, mark «выигрыш» /
 * «проигрыш», delete an empty one. The database keeps the rules (a lead
 * pipeline has one won and at least one lost stage, a stage with records
 * cannot be deleted) and its refusal is shown as it is.
 */
export const PipelineEditor = ({ pipeline }: { pipeline: Pipeline }) => {
  const translate = useTranslate();
  const run = useProjectAdmin();
  const { data } = useGetList<PipelineStage>("pipeline_stages", {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "position", order: "ASC" },
    filter: { pipeline_id: pipeline.id },
  });
  const saved = useMemo(() => [...(data ?? [])].sort(byPosition), [data]);
  // the order shown while a drag is being saved
  const [stages, setStages] = useState(saved);
  useEffect(() => setStages(saved), [saved]);
  const [newName, setNewName] = useState("");
  const [toDelete, setToDelete] = useState<PipelineStage | null>(null);

  const onDragEnd: OnDragEndResponder = ({ source, destination }) => {
    if (!destination || destination.index === source.index) return;
    const ordered = [...stages];
    const [moved] = ordered.splice(source.index, 1);
    ordered.splice(destination.index, 0, moved);
    setStages(ordered);
    run((dataProvider) => renumber(dataProvider, ordered));
  };

  const addStage = async () => {
    const name = newName.trim();
    if (!name) return;
    // a new stage goes before the won / lost ones, among the working stages
    const firstClosed = stages.findIndex((s) => s.outcome !== "open");
    const at = firstClosed === -1 ? stages.length : firstClosed;
    await run(async (dataProvider) => {
      const { data: created } = await dataProvider.create<PipelineStage>(
        "pipeline_stages",
        {
          data: {
            pipeline_id: pipeline.id,
            name,
            outcome: "open",
            position: (at + 1) * 10 - 5,
          },
        },
      );
      const ordered = [...stages];
      ordered.splice(at, 0, created);
      await renumber(dataProvider, ordered);
    });
    setNewName("");
  };

  const update = (stage: PipelineStage, change: Partial<PipelineStage>) =>
    run((dataProvider) =>
      dataProvider.update<PipelineStage>("pipeline_stages", {
        id: stage.id,
        data: change,
        previousData: stage,
      }),
    );

  return (
    <div className="flex flex-col gap-3">
      {pipeline.kind === "lead" ? (
        <p className="text-xs text-muted-foreground">
          {translate("crm.projects.lead_rules")}
        </p>
      ) : null}
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId={`pipeline-${pipeline.id}`}>
          {(droppable) => (
            <ol
              ref={droppable.innerRef}
              {...droppable.droppableProps}
              aria-label={translate("crm.projects.stages")}
              className="flex flex-col gap-1.5"
            >
              {stages.map((stage, index) => (
                <Draggable
                  key={stage.id}
                  draggableId={String(stage.id)}
                  index={index}
                >
                  {(draggable, snapshot) => (
                    <li
                      ref={draggable.innerRef}
                      {...draggable.draggableProps}
                      data-stage={stage.name}
                      className={cn(
                        "flex items-center gap-2 rounded-md border bg-card px-2 py-1.5",
                        snapshot.isDragging && "shadow-lg",
                      )}
                    >
                      <span
                        {...draggable.dragHandleProps}
                        aria-label={translate("crm.projects.drag")}
                        className="text-muted-foreground cursor-grab"
                      >
                        <GripVertical className="size-4" />
                      </span>
                      <StageName stage={stage} onSave={update} />
                      <select
                        aria-label={`${stage.name}: ${translate("crm.projects.outcome_label")}`}
                        value={stage.outcome}
                        onChange={(event) =>
                          update(stage, {
                            outcome: event.target.value as StageOutcome,
                          })
                        }
                        className={cn(
                          "h-8 rounded-md border bg-card px-2 text-xs",
                          stage.outcome === "won" && "text-chart-3",
                          stage.outcome === "lost" && "text-destructive",
                        )}
                      >
                        {OUTCOMES.map((outcome) => (
                          <option key={outcome} value={outcome}>
                            {translate(`crm.projects.outcome.${outcome}`)}
                          </option>
                        ))}
                      </select>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={`${translate("crm.projects.delete_stage")} ${stage.name}`}
                        onClick={() => setToDelete(stage)}
                      >
                        <Trash2 className="size-4" />
                      </Button>
                    </li>
                  )}
                </Draggable>
              ))}
              {droppable.placeholder}
            </ol>
          )}
        </Droppable>
      </DragDropContext>
      <Confirm
        isOpen={toDelete != null}
        title="crm.projects.confirm_delete_stage"
        titleTranslateOptions={{ name: toDelete?.name ?? "" }}
        content="crm.projects.confirm_delete_hint"
        confirm="ra.action.delete"
        confirmColor="warning"
        onClose={() => setToDelete(null)}
        onConfirm={() => {
          const stage = toDelete;
          setToDelete(null);
          if (stage)
            run((dataProvider) =>
              dataProvider.delete("pipeline_stages", {
                id: stage.id,
                previousData: stage,
              }),
            );
        }}
      />
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          addStage();
        }}
      >
        <Input
          value={newName}
          onChange={(event) => setNewName(event.target.value)}
          placeholder={translate("crm.projects.new_stage")}
          aria-label={translate("crm.projects.new_stage")}
          className="h-8"
        />
        <Button type="submit" variant="outline" size="sm">
          <Plus className="size-4" />
          {translate("crm.projects.add_stage")}
        </Button>
      </form>
    </div>
  );
};

/** Saved when the field loses focus or on Enter. */
const StageName = ({
  stage,
  onSave,
}: {
  stage: PipelineStage;
  onSave: (stage: PipelineStage, change: Partial<PipelineStage>) => unknown;
}) => {
  const translate = useTranslate();
  const [name, setName] = useState(stage.name);
  useEffect(() => setName(stage.name), [stage.name]);
  const save = () => {
    const trimmed = name.trim();
    if (!trimmed) setName(stage.name);
    else if (trimmed !== stage.name) onSave(stage, { name: trimmed });
  };
  return (
    <Input
      value={name}
      onChange={(event) => setName(event.target.value)}
      onBlur={save}
      onKeyDown={(event) => {
        if (event.key === "Enter") {
          event.preventDefault();
          save();
        }
      }}
      aria-label={translate("crm.projects.stage_name")}
      className="h-8 flex-1 min-w-0"
    />
  );
};
